<script setup lang="ts">
import type { AwTableColumn } from "@asap/design";
import type { RemunerationTemplate } from "@asap/shared/schemas/remuneration/remuneration-template";
import { formatRemunerationCoefficient, formatRemunerationDecimal } from "@asap/shared/utils/remuneration";
import type { TableRowAction } from "~/components/shared/table/TableRowActions.vue";

defineProps<{
  templates: RemunerationTemplate[];
  loading: boolean;
  canEdit?: boolean;
  missingOrganization?: boolean;
}>();

const emit = defineEmits<{
  create: [];
  edit: [template: RemunerationTemplate];
  duplicate: [template: RemunerationTemplate];
  delete: [template: RemunerationTemplate];
}>();

const { t } = useI18n();

const tableColumns: AwTableColumn<RemunerationTemplate>[] = [
  { accessorKey: "name", header: t("companies.remuneration.templates.columnHeader") },
];

const overflowActions = (template: RemunerationTemplate): TableRowAction[] => [
  {
    label: t("companies.remuneration.templates.duplicateButton"),
    icon: "i-ph-copy",
    onClick: () => emit("duplicate", template),
  },
  {
    label: t("companies.remuneration.templates.deleteButton"),
    icon: "i-ph-trash",
    color: "critical",
    onClick: () => emit("delete", template),
  },
];
</script>

<template>
  <div class="templates-section">
    <div class="templates-header">
      <h3 class="templates-title">{{ $t("companies.remuneration.templates.title") }}</h3>
      <p class="templates-description">{{ $t("companies.remuneration.templates.description") }}</p>
    </div>

    <div v-if="loading" class="templates-skeleton">
      <AwSkeleton v-for="idx in 3" :key="idx" class="h-20 w-full rounded-lg mb-3" />
    </div>

    <AwBanner
      v-else-if="missingOrganization"
      color="secondary"
      icon="i-ph-info"
      :text="$t('companies.remuneration.templates.missingOrganization')"
    />

    <div v-else class="templates-table-wrapper">
      <div class="templates-table-header">
        <span class="templates-count">
          {{ $t("companies.remuneration.templates.count", templates.length) }}
        </span>
        <AwButton color="secondary" variant="solid" :disabled="canEdit === false" @click="canEdit && emit('create')">
          {{ $t("companies.remuneration.templates.createButton") }}
        </AwButton>
      </div>
      <AwTable
        :data="templates"
        :columns="tableColumns"
        :loading="false"
        :empty="$t('companies.remuneration.templates.empty')"
      >
        <template #empty>
          <span class="text-secondary-800">{{ $t("companies.remuneration.templates.empty") }}</span>
        </template>
        <template #name-cell="{ row }">
          <div class="template-cell">
            <div class="template-name">{{ row.original.name }}</div>
            <div class="template-badges">
              <AwBadge variant="light" color="secondary" size="sm" :is-rounded="true">
                {{ $t("companies.remuneration.templates.primesCount", row.original.profile.indemnities.length) }}
              </AwBadge>
              <AwBadge variant="light" color="secondary" size="sm" :is-rounded="true">
                {{
                  $t("companies.remuneration.templates.hourlyRate", {
                    rate: formatRemunerationDecimal(row.original.profile.hourly_rate),
                  })
                }}
              </AwBadge>
              <AwBadge variant="light" color="secondary" size="sm" :is-rounded="true">
                {{
                  $t("companies.remuneration.templates.hoursCoefficient", {
                    coefficient: formatRemunerationCoefficient(row.original.profile.hours_coefficient),
                  })
                }}
              </AwBadge>
              <AwBadge variant="light" color="secondary" size="sm" :is-rounded="true">
                {{
                  $t("companies.remuneration.templates.bonusCoefficient", {
                    coefficient: formatRemunerationCoefficient(row.original.profile.bonus_coefficient),
                  })
                }}
              </AwBadge>
            </div>
          </div>
        </template>
        <template v-if="canEdit !== false" #actions="{ row }">
          <div class="template-actions">
            <AwButton variant="outline" color="secondary" size="sm" @click="emit('edit', row.original)">
              {{ $t("companies.remuneration.templates.editButton") }}
            </AwButton>
            <TableRowActions :row="row" :actions="overflowActions(row.original)" />
          </div>
        </template>
      </AwTable>
    </div>
  </div>
</template>

<style scoped lang="scss">
.templates-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

.templates-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.templates-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.templates-description {
  font-size: 13px;
  color: var(--gray-2);
  margin: 0;
}

.templates-table-wrapper {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--gray-5);
  border-radius: 12px;
  overflow: hidden;
}

.templates-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--gray-5);
}

.templates-table-wrapper :deep(thead) {
  display: none;
}

.templates-table-wrapper :deep(tbody tr) {
  background-color: white;
}

.templates-count {
  font-size: 13px;
  font-weight: 600;
}

.templates-skeleton {
  min-height: 120px;
}

.template-cell {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-name {
  font-weight: 600;
  font-size: 14px;
}

.template-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.template-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
}
</style>
